import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Market } from "@/hooks/useAdminPayroll";

interface MarketsTabProps {
  markets: Market[];
  onAddMarket: (
    market: Omit<Market, "id" | "created_at" | "updated_at">
  ) => void;
  onUpdateMarket: (id: string, updates: Partial<Market>) => void;
  onDeleteMarket: (id: string) => void;
}

const emptyMarketForm = {
  name: "",
  country: "",
  flag_emoji: "",
  salary_multiplier: 1,
  work_week_hours: 40,
  annual_leave_days: 25,
  social_charges_percent: 30,
};

export function MarketsTab({
  markets,
  onAddMarket,
  onUpdateMarket,
  onDeleteMarket,
}: MarketsTabProps) {
  const [formData, setFormData] = useState(emptyMarketForm);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMarket, setEditingMarket] = useState<Market | null>(null);

  const openAddDialog = () => {
    resetForm();
    setEditingMarket(null);
    setIsDialogOpen(true);
  };

  const openEditDialog = (market: Market) => {
    setFormData({
      name: market.name,
      country: market.country,
      flag_emoji: market.flag_emoji,
      salary_multiplier: market.salary_multiplier,
      work_week_hours: market.work_week_hours,
      annual_leave_days: market.annual_leave_days,
      social_charges_percent: market.social_charges_percent,
    });
    setEditingMarket(market);
    setIsDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!formData.name || !formData.country) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (editingMarket) {
      onUpdateMarket(editingMarket.id, formData);
      toast.success("Market updated");
    } else {
      onAddMarket({
        ...formData,
        is_active: true,
      });
      toast.success("Market added");
    }

    resetForm();
    setEditingMarket(null);
    setIsDialogOpen(false);
  };

  const resetForm = () => {
    setFormData(emptyMarketForm);
  };

  const handleFormChange = (
    field: keyof typeof formData,
    value: string | number
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const renderMarketForm = () => (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="market-name">City/Region</Label>
          <Input
            id="market-name"
            value={formData.name}
            onChange={(e) => handleFormChange("name", e.target.value)}
            placeholder="e.g., Amsterdam"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="market-country">Country</Label>
          <Input
            id="market-country"
            value={formData.country}
            onChange={(e) => handleFormChange("country", e.target.value)}
            placeholder="e.g., Netherlands"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="market-flag">Flag Emoji</Label>
          <Input
            id="market-flag"
            value={formData.flag_emoji}
            onChange={(e) => handleFormChange("flag_emoji", e.target.value)}
            placeholder="🇳🇱"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="market-multiplier">Salary Multiplier</Label>
          <Input
            id="market-multiplier"
            type="number"
            step="0.05"
            value={formData.salary_multiplier}
            onChange={(e) =>
              handleFormChange("salary_multiplier", Number(e.target.value))
            }
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="market-workWeek">Work Week (hrs)</Label>
          <Input
            id="market-workWeek"
            type="number"
            value={formData.work_week_hours}
            onChange={(e) =>
              handleFormChange("work_week_hours", Number(e.target.value))
            }
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="market-leave">Annual Leave</Label>
          <Input
            id="market-leave"
            type="number"
            value={formData.annual_leave_days}
            onChange={(e) =>
              handleFormChange("annual_leave_days", Number(e.target.value))
            }
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="market-charges">Social Charges %</Label>
          <Input
            id="market-charges"
            type="number"
            value={formData.social_charges_percent}
            onChange={(e) =>
              handleFormChange("social_charges_percent", Number(e.target.value))
            }
          />
        </div>
      </div>
    </>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Labor Markets</h2>
        <Button className="bg-primary-blue text-white" onClick={openAddDialog}>
          <Plus className="h-4 w-4 mr-2" />
          Add Market
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Market</TableHead>
                <TableHead>Country</TableHead>
                <TableHead className="text-center">Multiplier</TableHead>
                <TableHead className="text-center">Work Week</TableHead>
                <TableHead className="text-center">Annual Leave</TableHead>
                <TableHead className="text-center">Social Charges</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {markets
                .filter((m) => m.is_active)
                .map((market) => (
                  <TableRow key={market.id}>
                    <TableCell className="font-medium">
                      {market.flag_emoji} {market.name}
                    </TableCell>
                    <TableCell>{market.country}</TableCell>
                    <TableCell className="text-center">
                      ×{market.salary_multiplier}
                    </TableCell>
                    <TableCell className="text-center">
                      {market.work_week_hours}h
                    </TableCell>
                    <TableCell className="text-center">
                      {market.annual_leave_days} days
                    </TableCell>
                    <TableCell className="text-center">
                      {market.social_charges_percent}%
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(market)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeleteMarket(market.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="p-0 max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex-1 overflow-y-auto p-6">
            <DialogHeader className="pb-4">
              <DialogTitle>
                {editingMarket ? "Edit Market" : "Add New Market"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">{renderMarketForm()}</div>
          </div>

          <div className="p-6 shrink-0">
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  resetForm();
                  setEditingMarket(null);
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-primary-blue text-white"
                onClick={handleSubmit}
              >
                {editingMarket ? "Save" : "Add Market"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
